import React, { useMemo } from 'react';
import moment from 'moment';

// functions
import * as ComponentFunctions from './PracitionerProtocolActivity.Functions';
import { PractitionerProtocolActivityDay } from './PractitionerProtocolActivity.Day/PractitionerProtocolActivity.Day.Component';

interface MonthProps {
    month: number;
    year: number;
    visitsByDateKey: Map<string, number>;
}

const PractitionerProtocolActivityMonthBase = (props: MonthProps) => {

    // Weeks of this month (memoized — pure date math)
    const weeks = useMemo(
        () => ComponentFunctions.getWeeksOfMonth(props.month, props.year),
        [props.month, props.year]
    );

    // Monthly total — derived from the shared visits map (no per-day Redux lookups)
    const monthTotal = useMemo(() => {
        let total = 0;
        const prefix = `${props.year}-${String(props.month + 1).padStart(2, '0')}-`;
        props.visitsByDateKey.forEach((count, key) => {
            if (key.startsWith(prefix)) total += count;
        });
        return total;
    }, [props.visitsByDateKey, props.month, props.year]);

    const monthName = useMemo(
        () => ComponentFunctions.getMonthName(props.month),
        [props.month]
    );

    const weekDays = useMemo(() => ComponentFunctions.getWeekDays(), []);
    const todayKey = useMemo(() => moment().format('YYYY-MM-DD'), []);

    return (
        <div className="activity-monthCard">
            <div className="activity-monthHeader">
                <span className="activity-monthEyebrow">Actividad</span>
                <div className="activity-monthTitleRow">
                    <span className="activity-monthTitle">
                        {monthName}
                        <span className="activity-monthYear">{props.year}</span>
                    </span>
                    <span className="activity-monthTotal" title="Total de visitas en el mes">
                        <span className="activity-monthTotal-value">{monthTotal}</span>
                        <span className="activity-monthTotal-label">visitas</span>
                    </span>
                </div>
            </div>

            <table className="activity-grid" aria-label={`${monthName} ${props.year}`}>
                <thead>
                    <tr>
                        {weekDays.map((day, idx) => (
                            <th key={`wd-${idx}-${day}`} scope="col">{day}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {weeks.map((week) => {
                        const weekKey = moment(week[0]).format('YYYY-MM-DD');
                        return (
                            <tr key={weekKey}>
                                {week.map((day) => {
                                    const dKey = moment(day).format('YYYY-MM-DD');
                                    const inMonth = day.getMonth() === props.month;
                                    if (!inMonth) {
                                        return <td key={dKey}><span className="activity-cell activity-cell-inactive" aria-hidden="true" /></td>;
                                    }
                                    const count = props.visitsByDateKey.get(dKey) ?? 0;
                                    return (
                                        <td key={dKey}>
                                            <PractitionerProtocolActivityDay
                                                day={day}
                                                visitCount={count}
                                                isToday={dKey === todayKey}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export const PractitionerProtocolActivityMonth = React.memo(PractitionerProtocolActivityMonthBase);
